import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, Plus, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
// FIXED: was @ts-ignore - TODO: Fix this TypeScript error
import type { Contract, ContractService } from '@/lib/types/contracts';
import { TermUnit } from '@/lib/types/enums';

interface ContractFormProps {
  contract?: Contract | null;
  onSubmit: (data: Partial<Contract>) => Promise<void>;
}

export function ContractForm({ contract, onSubmit }: ContractFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(
    contract ? new Date(contract.startDate) : undefined
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    contract ? new Date(contract.endDate) : undefined
  );
// FIXED: was @ts-ignore - TODO: Fix this TypeScript error
  const [services, setServices] = useState<ContractService[]>(
    contract?.services || []
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData(e.currentTarget);
      const data: Partial<Contract> = {
        title: formData.get('title') as string,
        description: formData.get('description') as string,
        termLength: parseInt(formData.get('termLength') as string),
        termUnit: formData.get('termUnit') as TermUnit,
        value: parseFloat(formData.get('value') as string),
        startDate: startDate?.toISOString(),
// FIXED: was @ts-ignore - TODO: Fix this TypeScript error
        endDate: endDate?.toISOString(),
        autoRenew: formData.get('autoRenew') === 'true',
        renewalPreference: mapRenewalPreference(formData.get('renewalPreference') as string),
        services,
      };

      await onSubmit(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  const mapRenewalPreference = (value: string): 'automatic' | 'manual' | 'none' => {
    switch (value) {
      case 'auto':
        return 'automatic';
      case 'manual':
        return 'manual';
      case 'no_renewal':
        return 'none';
      default:
        return 'automatic';
    }
  };

  const addService = () => {
    setServices([
      ...services,
      {
        id: crypto.randomUUID(),
        name: '',
        description: '',
        frequency: 'monthly',
        price: 0,
        quantity: 1,
        unit: 'unit',
      } as ContractService,
    ]);
  };

  const updateService = (index: number, updates: Partial<ContractService>) => {
    setServices(services.map((service, i) => 
      i === index ? { ...service, ...updates } : service
    ));
  };

  const removeService = (index: number) => {
    setServices(services.filter((_, i) => i !== index));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{contract ? 'Edit Contract' : 'New Contract'}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title">Contract Title</Label>
            <Input
              id="title"
              name="title"
              defaultValue={contract?.title}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              name="description"
              defaultValue={contract?.description}
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="termLength">Term Length</Label>
              <Input
                id="termLength"
// FIXED: was @ts-ignore - TODO: Fix this TypeScript error
                name="termLength"
                type="number"
                min="1"
                defaultValue={contract?.termLength || 12}
                required
              />
// FIXED: was @ts-ignore - TODO: Fix this TypeScript error
            </div>

            <div className="space-y-2">
              <Label htmlFor="termUnit">Term Unit</Label>
              <Select name="termUnit" defaultValue={contract?.termUnit || TermUnit.MONTH}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TermUnit.MONTH}>Month(s)</SelectItem>
                  <SelectItem value={TermUnit.YEAR}>Year(s)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full pl-3 text-left font-normal',
                      !startDate && 'text-muted-foreground'
                    )}
                  >
                    {startDate ? (
                      format(startDate, 'PPP')
                    ) : (
                      <span>Pick a date</span>
                    )}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    disabled={(date) =>
                      date < new Date() || (endDate ? date > endDate : false)
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full pl-3 text-left font-normal',
                      !endDate && 'text-muted-foreground'
                    )}
                  >
                    {endDate ? (
                      format(endDate, 'PPP')
                    ) : (
                      <span>Pick a date</span>
                    )}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    disabled={(date) =>
                      date < (startDate || new Date())
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="value">Contract Value</Label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-muted-foreground">$</span>
              <Input
                id="value"
                name="value"
                type="number"
                step="0.01"
                min="0"
                className="pl-7"
                defaultValue={contract?.value || 0}
                required
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Contract Services</Label>
              <Button type="button" variant="outline" onClick={addService}>
                <Plus className="mr-2 h-4 w-4" />
                Add Service
              </Button>
            </div>

            <div className="space-y-4">
              {services.map((service, index) => (
                <div key={service.id} className="space-y-4 rounded-lg border p-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Service Name</Label>
                      <Input
                        value={service.name}
                        onChange={(e) =>
                          updateService(index, { name: e.target.value })
                        }
                        placeholder="Enter service name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Frequency</Label>
                      <Select
                        value={service.frequency}
                        onValueChange={(value: any) =>
                          updateService(index, { frequency: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="yearly">Yearly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={service.description}
                      onChange={(e) =>
                        updateService(index, { description: e.target.value })
                      }
                      placeholder="Describe the service"
                      rows={2}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <Label>Price</Label>
                      <div className="relative w-[200px]">
                        <span className="absolute left-3 top-2.5 text-muted-foreground">
                          $
                        </span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          className="pl-7"
                          value={service.price}
                          onChange={(e) =>
                            updateService(index, {
                              price: parseFloat(e.target.value),
                            })
                          }
                        />
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => removeService(index)}
                    >
                      Remove Service
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {contract ? 'Update Contract' : 'Create Contract'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}